import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { PageHeader, EmptyState } from '@shared/components';
import { useApiQuery } from '@shared/hooks/useApi.js';
import { api } from '@core/api.js';
import { useNotify } from '@shared/hooks/useNotify.js';
import { formatDateTime } from '@shared/utils/formatters.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';
import ContactFormDialog from '../components/ContactFormDialog.jsx';

const STATUS_LABELS = {
  lead: 'Lead',
  qualified: 'Qualifié',
  customer: 'Client',
  lost: 'Perdu',
};

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const {
    data: c, isLoading, isError, error, refetch,
  } = useApiQuery(['contact', id], `/v1/contacts/${id}`);
  const [editing, setEditing] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const enableBooking = async () => {
    if (!c?.id) return;
    if (!c.email && !c.phone) {
      notify.error('Ajoutez un e-mail ou un téléphone avant d’activer la réservation en ligne.');
      setEditing(true);
      return;
    }
    setEnabling(true);
    try {
      await api.post(`/v1/contacts/${c.id}/enable-booking`);
      notify.success('Réservation en ligne activée — la personne peut prendre RDV sur le site.');
      refetch();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Activation de la réservation impossible.'));
    } finally {
      setEnabling(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={() => refetch()}>Réessayer</Button>}
      >
        {apiErrorMessage(error, 'Chargement du contact impossible.')}
      </Alert>
    );
  }

  if (!c) {
    return (
      <EmptyState
        title="Contact introuvable"
        description="Ce contact n’est plus disponible."
        action={{ label: 'Retour aux contacts', onClick: () => navigate('/contacts') }}
      />
    );
  }

  const canBook = !!c.homeMeetingCompleted && !!(c.email || c.phone);

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={c.email || c.phone || ''}
        breadcrumbs={[{ label: 'Contacts', to: '/contacts' }, { label: c.name }]}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!canBook && (
              <Button
                startIcon={<EventAvailableIcon />}
                variant="contained"
                disabled={enabling}
                onClick={enableBooking}
              >
                Activer la réservation en ligne
              </Button>
            )}
            <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEditing(true)}>
              Modifier
            </Button>
          </Stack>
        )}
      />
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {canBook ? (
            <Alert severity="success">
              Peut prendre RDV sur le site (page réservation → « Déjà client(e) »).
            </Alert>
          ) : (
            <Alert severity="info">
              Première visite en attente ou réservation non activée. Après la rencontre, utilisez
              « Activer la réservation en ligne ».
            </Alert>
          )}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Chip label={STATUS_LABELS[c.status] || c.status} color="primary" />
            {c.source && <Chip label={`Source : ${c.source}`} variant="outlined" />}
            <Chip
              label={c.homeMeetingCompleted ? 'Réservation en ligne autorisée' : 'Réservation en ligne désactivée'}
              color={c.homeMeetingCompleted ? 'success' : 'default'}
              variant={c.homeMeetingCompleted ? 'filled' : 'outlined'}
            />
          </Stack>
          <Divider />
          <Typography><strong>Email :</strong> {c.email || '—'}</Typography>
          <Typography><strong>Téléphone :</strong> {c.phone || '—'}</Typography>
          <Typography><strong>Société :</strong> {c.company || '—'}</Typography>
          <Stack direction="row" spacing={1}>
            <Typography><strong>Tags :</strong></Typography>
            {(c.tags || []).map((t) => <Chip key={t} size="small" label={t} />)}
          </Stack>
          {c.notes && <Typography><strong>Notes :</strong> {c.notes}</Typography>}
          <Divider />
          <Typography variant="caption" color="text.secondary">
            Créé le {formatDateTime(c.createdAt)} · Mis à jour le {formatDateTime(c.updatedAt)}
          </Typography>
        </Stack>
      </Paper>
      {editing && (
        <ContactFormDialog
          contact={c}
          onClose={(didMutate) => {
            setEditing(false);
            if (didMutate) refetch();
          }}
        />
      )}
    </>
  );
}
